import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import {
  DriverBadge,
  driverLabel,
  fmtSignedPct,
  deltaTone,
  type IndexRow,
  LabelBadge,
  OriginBadge,
  type PageMeta,
  Pagination,
  partyHeader,
  periodLabel,
  tierLabel,
} from "./shared"

type Filters = { search: string | null; label: string | null; driver_type: string | null; period: string | null; tier: string | null }
type Props = {
  rows: IndexRow[]
  filters: Filters
  filter_options: { labels: string[]; driver_types: string[]; periods: string[]; tiers: string[] }
  counts: { moves: number; identified: number; unexplained: number }
  meta: PageMeta
}

const PATH = "/admin/sell-in/drivers"
const DESCRIPTION =
  "Kenapa sell-in sebuah produk ke sebuah akun bergerak — dekomposisi deterministik + pendorong ter-ranking dan ter-citasi (bukti, bukan klaim sebab)."

const LABEL_TEXT: Record<string, string> = { identified: "Teridentifikasi", unexplained: "Belum terjelaskan" }

export default function Index({ rows, filters, filter_options, counts, meta }: Props) {
  const [search, setSearch] = React.useState(filters.search ?? "")

  const navigate = (patch: Partial<Filters & { page: number }>) => {
    const merged: Record<string, string> = {}
    const base = { search: filters.search, label: filters.label, driver_type: filters.driver_type, period: filters.period, tier: filters.tier, ...patch }
    Object.entries(base).forEach(([k, v]) => {
      if (v != null && v !== "") merged[k] = String(v)
    })
    router.get(PATH, merged, { preserveState: true, preserveScroll: true, replace: true })
  }

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault()
    navigate({ search: search.trim(), page: 1 })
  }

  return (
    <>
      <Head title="Driver Sell-In">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Driver Sell-In" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell width="wide">
        <div className="border-b border-hairline pb-6">
          <h1>Driver Sell-In</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Kenapa sell-in produk ke akun bergerak — pendorong ter-ranking dan ter-citasi. Ko-okurensi sebagai bukti, bukan klaim
            sebab-akibat.
          </p>
        </div>

        {/* Counts */}
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard title="Pergerakan material" value={counts.moves} />
          <StatCard title="Teridentifikasi" value={counts.identified} tone="success" />
          <StatCard title="Belum terjelaskan" value={counts.unexplained} tone="muted" />
        </div>

        {/* Echelon (tier) selector — only when the data carries more than one echelon. */}
        {filter_options.tiers.length > 1 && (
          <div className="mt-6">
            <label htmlFor="sid-tier">Eselon (echelon)</label>
            <div className="mt-1 inline-flex flex-wrap gap-2" role="group" aria-label="Eselon">
              {filter_options.tiers.map((t) => {
                const active = (filters.tier ?? "SS") === t
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => navigate({ tier: t, page: 1 })}
                    aria-pressed={active}
                    className={`rounded-md border px-3 py-1.5 text-sm ${
                      active ? "border-accent bg-accent/10 text-ink-display" : "border-hairline bg-surface text-ink-muted hover:text-ink-body"
                    }`}
                  >
                    {tierLabel(t)}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="mt-6 flex flex-wrap items-end gap-3">
          <form onSubmit={submitSearch} className="min-w-[220px] flex-1">
            <label htmlFor="sid-search">Cari produk / akun</label>
            <Input
              id="sid-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="mis. Vitalis, Indomaret…"
              className="mt-1"
            />
          </form>
          <div className="min-w-[150px]">
            <label htmlFor="sid-label">Status</label>
            <Select
              id="sid-label"
              className="mt-1"
              value={filters.label ?? ""}
              onChange={(e) => navigate({ label: e.target.value, page: 1 })}
            >
              <option value="">Semua status</option>
              {filter_options.labels.map((l) => (
                <option key={l} value={l}>
                  {LABEL_TEXT[l] ?? l}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-[170px]">
            <label htmlFor="sid-driver">Pendorong utama</label>
            <Select
              id="sid-driver"
              className="mt-1"
              value={filters.driver_type ?? ""}
              onChange={(e) => navigate({ driver_type: e.target.value, page: 1 })}
            >
              <option value="">Semua pendorong</option>
              {filter_options.driver_types.map((t) => (
                <option key={t} value={t}>
                  {driverLabel(t)}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-[140px]">
            <label htmlFor="sid-period">Periode</label>
            <Select
              id="sid-period"
              className="mt-1"
              value={filters.period ?? ""}
              onChange={(e) => navigate({ period: e.target.value, page: 1 })}
            >
              <option value="">Semua periode</option>
              {filter_options.periods.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {/* Desktop table */}
        {rows.length === 0 ? (
          <p className="mt-8 text-sm text-ink-muted">Tidak ada pergerakan yang cocok dengan filter.</p>
        ) : (
          <>
            <div className="mt-6 hidden overflow-x-auto rounded-lg border border-hairline md:block">
              <table className="w-full text-sm">
                <thead className="bg-surface text-left text-ink-muted">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-medium">Produk</th>
                    <th scope="col" className="px-4 py-3 font-medium">{partyHeader(filters.tier ?? "SS")}</th>
                    <th scope="col" className="px-4 py-3 font-medium">Periode</th>
                    <th scope="col" className="px-4 py-3 font-medium">Δ MoM</th>
                    <th scope="col" className="px-4 py-3 font-medium">Pendorong utama</th>
                    <th scope="col" className="px-4 py-3 font-medium">Lokalisasi</th>
                    <th scope="col" className="px-4 py-3 font-medium">Status</th>
                    <th scope="col" className="px-4 py-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-t border-hairline hover:bg-surface/60">
                      <td className="px-4 py-3 font-medium text-ink-body">{row.product}</td>
                      <td className="px-4 py-3 text-ink-body">{row.party}</td>
                      <td className="px-4 py-3 tabular-nums text-ink-muted">{periodLabel(row.period)}</td>
                      <td className="px-4 py-3">
                        <Badge tone={deltaTone(row.delta_mom_pct)}>{fmtSignedPct(row.delta_mom_pct)}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <DriverBadge type={row.lead_driver} />
                        {row.driver_count > 1 && <span className="ml-1 text-xs text-ink-muted">+{row.driver_count - 1}</span>}
                      </td>
                      <td className="px-4 py-3">
                        <OriginBadge originTier={row.origin_tier} pattern={row.pattern} />
                      </td>
                      <td className="px-4 py-3">
                        <LabelBadge label={row.lead_label} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link href={`${PATH}/${row.id}`} className="text-sm text-accent hover:underline">
                          Kenapa →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <ul className="mt-6 space-y-3 md:hidden">
              {rows.map((row) => (
                <li key={row.id}>
                  <Link
                    href={`${PATH}/${row.id}`}
                    className="block rounded-lg border border-hairline bg-surface p-4 hover:border-accent"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-medium text-ink-body">{row.product}</span>
                      <Badge tone={deltaTone(row.delta_mom_pct)}>{fmtSignedPct(row.delta_mom_pct)}</Badge>
                    </div>
                    <p className="mt-0.5 text-sm text-ink-muted">
                      {row.party} · {periodLabel(row.period)}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <DriverBadge type={row.lead_driver} />
                      <OriginBadge originTier={row.origin_tier} pattern={row.pattern} />
                      <LabelBadge label={row.lead_label} />
                      {row.driver_count > 1 && <span className="text-xs text-ink-muted">+{row.driver_count - 1} pendorong</span>}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}

        <Pagination meta={meta} path={PATH} query={{ ...filters }} />
      </AppShell>
    </>
  )
}

function StatCard({ title, value, tone }: { title: string; value: number; tone?: "success" | "muted" }) {
  const color = tone === "success" ? "text-success" : tone === "muted" ? "text-ink-muted" : "text-ink-display"
  return (
    <div className="rounded-lg border border-hairline bg-surface p-4">
      <span className="text-xs text-ink-muted">{title}</span>
      <div className={`mt-2 font-display text-2xl ${color}`}>{new Intl.NumberFormat("id-ID").format(value)}</div>
    </div>
  )
}
